<template>
	<div>
		<DetailsLayout
			:loading="isLoading"
			:class="['connection-details', integrationType]"
		>
			<template #super-title>
				<div class="back-button" v-if="!isLoading">
					<Button
						link
						:to="{ name: 'integrations', params: { type: 'CONNECTED' } }"
					>
						<ChevronIcon class="back-button-icon" type="regular" left />
						<span v-text="t('admin_overview_integrations_title')" />
					</Button>
				</div>
			</template>

			<template #title>
				<div class="logo-title-container" v-if="!isLoading">
					<div class="logo" v-if="connection?.integration?.icon_url">
						<img
							:src="connection?.integration?.icon_url"
							alt="Integration logo"
						/>
					</div>
					<div class="title-content">
						<h1 class="details-title-with-logo">
							{{ connection?.integration?.display_name }}
						</h1>
						<div class="description-section">
							<div v-if="connection?.label" class="description-display">
								<span class="description-text">{{ connection?.label }}</span>
								<Button
									link
									@click="editDescription"
									class="description-btn edit"
								>
									<span>{{ t('admin_integrations_edit') }}</span>
								</Button>
							</div>

							<Button
								v-else
								link
								@click="editDescription"
								class="description-btn"
							>
								<span>{{ t('admin_integrations_add_description') }}</span>
							</Button>
						</div>
					</div>
				</div>
			</template>

			<template #content>
				<component
					v-if="connectionDetailsComponent && !isLoading"
					:is="connectionDetailsComponent"
					:connection="connection"
					:configuration="configuration"
					:platform-configuration="platformConfiguration"
					:is-loading="isLoading"
					content-only
				/>
				<div v-else>
					<SkeletonBar
						:class="['content-skeleton', integrationType]"
					></SkeletonBar>
				</div>
			</template>

			<template #table>
				<div class="table-wrapper">
					<div class="table-header">
						{{ t('admin_integrations_resources') }}
					</div>
					<div class="search-bar-container">
						<Input
							v-model="query"
							class="search-bar"
							:placeholder="'Search Resources'"
							:icon="SearchIcon"
							:iconProps="{ type: 'regular' }"
							allowClear
						/>
					</div>
					<Table
						class="resources-table"
						:columns="columns"
						:rows="resources"
						clickable
						infinite
						closePopoversOnScroll
						:loadingOverlay="fetchingResources"
						:defaultSort="sort"
						:error="fetchResourcesError"
						headerSize="small"
						@row-click="goToResourceDetails"
						@sort="onSort"
						@retry="fetchResources"
						height="calc(100vh - 296px)"
						ref="table"
						@load-more="loadMore"
						:loadingMore="loadingMore"
					>
						<template #cell-last_sync_at="{ data }">
							{{
								data.last_sync_at ? $m(data.last_sync_at).format('lll') : '--'
							}}
						</template>
						<template #cell-next_sync_at="{ data }">
							{{
								data.next_sync_at ? $m(data.next_sync_at).format('lll') : '--'
							}}
						</template>
					</Table>
				</div>
			</template>

			<template #sidebar>
				<component
					v-if="connectionDetailsComponent"
					:is="connectionDetailsComponent"
					:key="sidebarKey"
					:connection="connection"
					:configuration="configuration"
					:platform-configuration="platformConfiguration"
					:is-loading="isLoading"
					sidebar-only
					:is-syncing-all="isSyncingAll"
					:is-saving-configuration="isConfigurePending"
					@settingChange="handleConfigurationChange"
					@execFullSync="showSyncAllConfirm = true"
					@editRoleMappings="showRoleMappingModal = true"
					@delete="showDeleteModal = true"
				/>
			</template>
		</DetailsLayout>

		<DeleteModal
			v-if="showDeleteModal"
			@close="showDeleteModal = false"
			@delete="deleteConnection"
			:title="t('admin_integrations_delete_connection')"
			:showConfirm="true"
			:isProcessing="isDeleting"
			:bodyText="t('admin_integrations_delete_modal_body')"
			data-cy="deleteModal"
			:checkboxText="t('admin_integrations_delete_modal_confirm')"
		/>

		<StandardModal
			v-if="showEditDescription"
			:title="
				connection?.description
					? t('admin_integrations_edit_description')
					: t('admin_integrations_add_description')
			"
			open
			:confirmText="t('common_button_save')"
			:confirmProps="{
				disabled: !isDescriptionValid,
				loading: isSavingDescription,
			}"
			@confirm="saveDescription"
			@cancel="cancelEditDescription"
			class="edit-description-modal"
			small
		>
			<div class="edit-description-wrapper">
				<div class="edit-description-title">
					{{ t('admin_integrations_add_description_desc') }}
				</div>
				<div class="edit-description-input">
					<Input
						v-model="editingDescription"
						:placeholder="t('admin_integrations_description_placeholder')"
						maxlength="40"
					/>
				</div>
			</div>
		</StandardModal>

		<StandardModal
			v-if="showSyncAllConfirm"
			open
			:title="t('admin_integrations_sync_all_resources')"
			:confirmText="t('admin_integrations_sync_all')"
			:confirmProps="{ loading: isSyncingAll, disabled: isConfigurePending }"
			@confirm="execFullSync"
			@cancel="showSyncAllConfirm = false"
			small
			ref="syncAllModal"
			class="sync-all-modal"
		>
			<div class="sync-all-modal-wrapper">
				<ArrowsRotateIcon halo type="regular" />
				{{ t('admin_integrations_sync_all_confirm_body') }}
			</div>
		</StandardModal>

		<RoleMappingModal
			v-if="showRoleMappingModal"
			:role-mappings="configuration?.role_mappings"
			:is-saving="isSavingRoleMappings"
			:is-blocked="isConfigurePending"
			ref="roleMappingModal"
			@save="saveRoleMappings"
			@cancel="showRoleMappingModal = false"
		/>
	</div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue'

// Import specific detail components
import LTIConnectionDetails from './ConnectionDetails/LTIConnectionDetails'
import GoogleWorkspaceConnectionDetails from './ConnectionDetails/GoogleWorkspaceConnectionDetails'
import RoleMappingModal from './ConnectionDetails/RoleMappingModal'
import DeleteModal from '@/components/DeleteModal'
import {
	StandardModal,
	useAlert,
	Input,
	Button,
	SkeletonBar,
	Table,
	ASC,
} from '@/components/common'
import { ChevronIcon, SearchIcon, ArrowsRotateIcon } from '@/icons'
import { useI18n } from 'vue-i18n'
import {
	integrationDelete,
	integrationPatch,
	integrationGet,
} from '@/api/integrations'
import { useRouter } from 'vue-router'
import { DetailsLayout } from '../common'
import debounce from 'debounce'

// Props
const props = defineProps({
	connectionId: { type: [String, Number], required: true },
	integrationType: { type: String, required: false },
})

// Reactive State
const { t } = useI18n()
const alert = useAlert()
const router = useRouter()
const isLoading = ref(false)
const showDeleteModal = ref(false)
const isDeleting = ref(false)
const showEditDescription = ref(false)
const showSyncAllConfirm = ref(false)
const isSyncingAll = ref(false)
const syncAllModal = ref(null)
const showRoleMappingModal = ref(false)
const roleMappingModal = ref(null)
const sidebarKey = ref(0)
// Every configure PATCH echoes launch_enabled/sync_enabled out of
// configuration.value, so overlapping writes send stale flags and clobber each
// other. All three operations share one in-flight flag.
const isConfigurePending = ref(false)
const isSavingRoleMappings = ref(false)
const editingDescription = ref('')
const isSavingDescription = ref(false)
const connection = ref(null)
const configuration = ref(null)
const platformConfiguration = ref(null)
const resources = ref([])
const query = ref(null)
const fetchingResources = ref(false)
const fetchResourcesError = ref(null)
const loadingMore = ref(false)
const sort = ref({ field: 'label', order: ASC, param: 'label' })
const apiResponse = ref(null)

const connectionDetailsComponents = {
	lti13: LTIConnectionDetails,
	google_workspace: GoogleWorkspaceConnectionDetails,
	// Add more as needed
}

const columns = ref([
	{
		field: 'label',
		label: t('admin_integrations_name'),
		sorter: true,
		ascSort: 'label',
		dscSort: '-label',
	},
	{
		field: 'last_sync_at',
		label: t('admin_integrations_last_sync'),
		sorter: true,
		ascSort: 'last_sync_at',
		dscSort: '-last_sync_at',
		minWidth: '225px',
	},
	{
		field: 'next_sync_at',
		label: t('admin_integrations_next_sync'),
		sorter: true,
		ascSort: 'next_sync_at',
		dscSort: '-next_sync_at',
		minWidth: '225px',
	},
	{
		field: 'sync_status_label',
		label: t('admin_integrations_sync_status'),
		sorter: true,
		ascSort: 'sync_status_label',
		dscSort: '-sync_status_label',
		// Without a width this column falls back to flex-basis 100% and splits
		// the slack with Name, which leaves it absurdly wide. The basis fits the
		// longest value ('Completed with warnings'); the floor lets it shrink.
		width: '220px',
		minWidth: '140px',
	},
	{
		// Active / Deactivated. Not sortable or filterable: the server's
		// allow-list only covers sync_status_label and rejects this one with a 400
		field: 'status_label',
		label: t('admin_integrations_status'),
		width: '150px',
		minWidth: '130px',
	},
	{
		field: 'external_id',
		label: 'External ID',
		minWidth: '120px',
	},
])

// Watchers
watch(query, (newQuery) => {
	if (newQuery) {
		debouncedFetchResources({
			'filter[label]': newQuery,
		})
	} else {
		fetchResources()
	}
})

// Computed
const connectionDetailsComponent = computed(() => {
	const integrationType =
		props.integrationType || connection.value?.integration?.type || 'default'

	return connectionDetailsComponents[integrationType] || LTIConnectionDetails
})

const integrationType = computed(() => {
	return (
		props.integrationType || connection.value?.integration?.type || 'default'
	)
})

const isDescriptionValid = computed(() => {
	return editingDescription.value !== connection.value?.label
})

// Methods
// const onSearch = async (query) => {
// 	console.log('Search:', query)
// 	const payload = {
// 		label: query,
// 	}
// 	await fetchResources(payload)
// }

const deleteConnection = async () => {
	isDeleting.value = true
	try {
		await integrationDelete(`/connections/${props.connectionId}`)
		isDeleting.value = false
		router.push({ name: 'integrations', params: { type: 'CONNECTED' } })
	} catch (e) {
		console.error(e)
		isDeleting.value = false
		alert.error(t('common_api_error_save'))
	}
}

const cancelEditDescription = () => {
	showEditDescription.value = false
	editingDescription.value = ''
}

const saveDescription = async () => {
	isSavingDescription.value = true
	try {
		connection.value = (
			await integrationPatch(`/connections/${props.connectionId}`, {
				label: editingDescription.value,
			})
		).data
		showEditDescription.value = false
		editingDescription.value = ''
		alert.success(t('admin_people_changes_saved_confirmation_label'))
	} catch (e) {
		console.error(e)
		alert.error(t('common_api_error_save'))
	} finally {
		isSavingDescription.value = false
	}
}

const fetchConnection = async () => {
	try {
		connection.value = (
			await integrationGet(`/connections/${props.connectionId}`)
		).data
	} catch (e) {
		console.error(e)
		alert.error(t('common_api_error_save'))
	}
}

const fetchConfiguration = async () => {
	configuration.value = (
		await integrationGet(
			`/${connection.value?.integration?.type}/configure/${props.connectionId}`
		)
	).data
}

const fetchPlatformConfiguration = async () => {
	platformConfiguration.value = (
		await integrationGet(
			`/${connection.value?.integration?.type}/configure/${props.connectionId}/${configuration.value?.platform}`
		)
	).data
}

const editDescription = () => {
	showEditDescription.value = true
	editingDescription.value = connection.value?.label
}

const fetchResources = async (payload = {}) => {
	payload.sort = sort.value.param
	fetchingResources.value = true
	try {
		apiResponse.value = await integrationGet(
			`/connections/${props.connectionId}/resources`,
			payload
		)
		resources.value = apiResponse.value.data
	} catch (error) {
		fetchResourcesError.value = error
	} finally {
		fetchingResources.value = false
	}
}

const debouncedFetchResources = debounce(fetchResources, 500)

const onSort = (newSort, { ascSort, dscSort }) => {
	sort.value = {
		...newSort,
		param: newSort.order === ASC ? ascSort : dscSort,
	}
	fetchResources()
}

const loadMore = async () => {
	if (!apiResponse.value?.next) return
	loadingMore.value = true
	try {
		apiResponse.value = await apiResponse.value.next()
		resources.value = resources.value.concat(apiResponse.value.data)
		loadingMore.value = false
	} catch (e) {
		console.error(e)
		loadingMore.value = false
		alert.error(t('common_api_error_save'))
	}
}

const goToResourceDetails = (row) => {
	router.push({
		name: 'resource',
		params: { connectionId: props.connectionId, resourceId: row.id },
		query: { integrationName: connection.value?.integration?.display_name },
	})
}

const configureUrl = computed(
	() =>
		`/${connection.value?.integration?.type}/configure/${props.connectionId}`
)

// The configure endpoint replaces rather than merges: anything absent from the
// PATCH body is cleared. Every call echoes the current persisted fields back
// alongside whatever it's actually changing, so flipping a toggle can't wipe
// role mappings (and vice versa). Keys that are undefined for a given provider
// (e.g. launch_enabled on Google Workspace) drop out when the body is
// serialized, so this stays a no-op for them.
const configurePayload = (data) => ({
	platform: configuration.value?.platform,
	launch_enabled: configuration.value?.launch_enabled,
	sync_enabled: configuration.value?.sync_enabled,
	remove_group_on_course_end: configuration.value?.remove_group_on_course_end,
	remove_group_days_after_course_end:
		configuration.value?.remove_group_days_after_course_end,
	role_mappings: configuration.value?.role_mappings,
	...data,
})

// These modals stay open on failure, so report the error inside the modal when
// one is up rather than behind it.
const alertSaveError = (modal) => {
	const modalAlert = modal?.$alert
	if (modalAlert) modalAlert.error(t('common_api_error_save'))
	else alert.error(t('common_api_error_save'))
}

const handleConfigurationChange = async (data) => {
	// Toggles are disabled while a write is pending, so this is a backstop:
	// resync the sidebar so a dropped click doesn't leave the switch sitting at
	// a setting that was never sent.
	if (isLoading.value || isConfigurePending.value) {
		sidebarKey.value++
		return
	}
	isConfigurePending.value = true

	try {
		configuration.value = (
			await integrationPatch(configureUrl.value, configurePayload(data))
		).data
		alert.success(t('admin_people_changes_saved_confirmation_label'))
	} catch (e) {
		console.error(e)
		alert.error(t('common_api_error_save'))
		// ToggleSwitch keeps its own copy of the value and only resyncs when
		// modelValue changes. A failed PATCH leaves `configuration` untouched,
		// so nothing recomputes and the switch stays flipped at a setting the
		// server never accepted — remount the sidebar to rebuild it from the
		// authoritative config.
		sidebarKey.value++
	} finally {
		isConfigurePending.value = false
	}
}

const execFullSync = async () => {
	if (isSyncingAll.value || isConfigurePending.value) return
	isSyncingAll.value = true
	isConfigurePending.value = true
	try {
		configuration.value = (
			await integrationPatch(
				configureUrl.value,
				configurePayload({ exec_full_sync: true })
			)
		).data
		showSyncAllConfirm.value = false
		alert.success(t('admin_integrations_sync_all_started'))
	} catch (e) {
		console.error(e)
		alertSaveError(syncAllModal.value)
	} finally {
		isSyncingAll.value = false
		isConfigurePending.value = false
	}
}

const saveRoleMappings = async (roleMappings) => {
	if (isSavingRoleMappings.value || isConfigurePending.value) return
	isSavingRoleMappings.value = true
	isConfigurePending.value = true
	try {
		configuration.value = (
			await integrationPatch(
				configureUrl.value,
				configurePayload({ role_mappings: roleMappings })
			)
		).data
		showRoleMappingModal.value = false
		alert.success(t('admin_integrations_role_mappings_saved'))
	} catch (e) {
		console.error(e)
		alertSaveError(roleMappingModal.value?.modal)
	} finally {
		isSavingRoleMappings.value = false
		isConfigurePending.value = false
	}
}

onMounted(async () => {
	isLoading.value = true
	fetchingResources.value = true
	await fetchConnection()
	await fetchConfiguration()
	if (configuration.value?.platform) {
		await fetchPlatformConfiguration()
	}
	await fetchResources()
	isLoading.value = false
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

:deep(.details-layout.connection-details.google_workspace) {
	grid-template-rows: 155px 50px auto;
}

:deep(.content-skeleton) {
	height: 40px;
	width: 100%;
}

:deep(.content-skeleton.lti13) {
	height: 115px;
}

:deep(.action-list-item.danger) {
	color: var(--red-400) !important;
	&:hover {
		color: var(--red-300) !important;
	}
}

:deep(.table-container .table-row .table-cell) {
	padding-left: 25px !important;
}

.connection-details {
	.back-button {
		margin-bottom: 10px;
		.pronto-btn {
			font-size: 14px;
			padding: 0;
			height: auto;
		}
		.back-button-icon {
			font-size: 12px;
		}
	}

	.logo-title-container {
		display: flex;
		align-items: center;
		margin: 0;

		.logo {
			height: 65px;
			width: 65px;
			border: solid 1px var(--neutral-200);
			display: flex;
			align-items: center;
			justify-content: center;
			border-radius: 6px;
			margin-right: 12px;
			flex-shrink: 0;

			img {
				height: 100%;
				width: 100%;
				border-radius: 4px;
				object-fit: contain;
			}
		}

		.details-title-with-logo {
			font-size: 36px;
			color: var(--neutral-800);
			font-family: Avenir-Heavy;
			margin: 0;
			@include clamp(2);
		}

		.description-section {
			.description-text {
				font-size: 14px;
				color: var(--neutral-600);
			}
			.description-btn {
				height: auto;
				padding: 0 !important;
				&.edit {
					margin-left: 8px;
				}
			}
		}
	}

	.table-wrapper {
		padding-top: 11px;
		.table-header {
			font-size: 24px;
			font-family: Avenir-Heavy;
			color: var(--neutral-800);
			margin-bottom: 20px;
			padding-left: 24px;
		}
		.resources-table {
			margin-top: 15px;
		}
		.search-bar-container {
			padding-right: 24px;
			padding-left: 24px;
		}
	}
}

.edit-description-wrapper {
	@include flex-column;
	gap: 36px;
	padding: 44px 50px;
	.edit-description-title {
		font-size: 16px;
		font-family: Avenir-Roman;
		color: var(--neutral-600);
		text-align: center;
	}
}

// Matches SendWelcomeModal's confirmation styling
.sync-all-modal-wrapper {
	@include flex-column-center;
	justify-content: center;
	height: 100%;
	max-width: 300px;
	margin: 0 auto;
	text-align: center;
	.pronto-icon {
		color: var(--blue-300);
		font-size: 50px;
		margin-bottom: 24px;
	}
}
</style>
